import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { logger } from "@/lib/logger";
import {
  ExternalLink, Loader2, AlertTriangle, RotateCcw, Target, Clock, TrendingUp, CheckCircle2, XCircle, MinusCircle,
} from "lucide-react";
import { SimLayout } from "@/sim/SimLayout";
import { getSim, getShot } from "@/sim/lib";
import { HeatmapOverlay } from "@/sim/components/HeatmapOverlay";
import { AgentFeed } from "@/sim/components/AgentFeed";
import { HealthGauge } from "@/sim/components/HealthGauge";
import { ObjectionRadar } from "@/sim/components/ObjectionRadar";
import { PersonaScorecard } from "@/sim/components/PersonaScorecard";
import { RemediationBacklog } from "@/sim/components/RemediationBacklog";
import { GrowthRecommendations } from "@/sim/components/GrowthRecommendations";
import { CompareDelta } from "@/sim/components/CompareDelta";
import { RerunPanel } from "@/sim/components/RerunPanel";
import { AppDetails } from "@/sim/components/AppDetails";

const TERMINAL = new Set(["COMPLETED", "FAILED"]);

function Stat({ icon: Icon, label, value, sub, color = "#F5F5F5" }) {
  return (
    <div className="sim-card rounded-xl p-4">
      <div className="flex items-center gap-1.5 mb-1">
        <Icon className="w-3.5 h-3.5 text-[#C2C5CE]" />
        <span className="sim-mono text-[10px] text-[#C2C5CE] uppercase tracking-wider">{label}</span>
      </div>
      <p className="sim-head text-2xl font-bold" style={{ color }}>{value}</p>
      {sub && <p className="sim-mono text-[10px] text-[#9AA0AC] mt-0.5">{sub}</p>}
    </div>
  );
}

export default function SimResults() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [sim, setSim] = useState(null);
  const [image, setImage] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [rerunOpen, setRerunOpen] = useState(false);
  const shotFetched = useRef(false);

  useEffect(() => {
    let timer;
    let alive = true;
    const poll = async () => {
      try {
        const d = await getSim(id);
        if (!alive) return;
        setSim(d);
        if (d.render_ready && !shotFetched.current) {
          shotFetched.current = true;
          getShot(id).then((img) => alive && setImage(img)).catch(() => {});
        }
        if (!TERMINAL.has(d.status)) timer = setTimeout(poll, 1800);
      } catch (e) {
        logger.error("poll failed:", e);
        if (alive) setNotFound(true);
      }
    };
    poll();
    return () => { alive = false; clearTimeout(timer); };
  }, [id]);

  if (notFound) {
    return (
      <SimLayout>
        <div className="text-center py-24">
          <AlertTriangle className="w-10 h-10 text-[#F59E0B] mx-auto mb-4" />
          <p className="sim-head text-xl font-bold text-[#F5F5F5]">Simulation not found</p>
          <button onClick={() => navigate("/sim")} className="mt-4 sim-mono text-sm text-[#00F0FF]">← Start a new simulation</button>
        </div>
      </SimLayout>
    );
  }

  if (!sim) {
    return (
      <SimLayout>
        <div className="py-24 flex flex-col items-center">
          <Loader2 className="w-8 h-8 text-[#00F0FF] animate-spin mb-3" />
          <p className="sim-mono text-[11px] text-[#C2C5CE]">INITIALIZING SWARM…</p>
        </div>
      </SimLayout>
    );
  }

  const agents = sim.agents || [];
  const s = sim.synthesis;
  const regions = agents.flatMap((a) => (a.attention_regions || []).map((r) => ({ ...r }))).slice(0, 36);
  const running = !TERMINAL.has(sim.status);
  const failed = sim.status === "FAILED";
  const isApp = sim.source_type === "app_upload";

  const statusLabel = {
    QUEUED: "Queued", RENDERING: "Rendering page", RUNNING: `Running · ${agents.length}/${sim.progress?.total || 9} agents`,
    COMPLETED: "Completed", FAILED: "Failed",
  }[sim.status] || sim.status;

  return (
    <SimLayout>
      {/* Header bar */}
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <span className={`sim-mono text-[10px] px-2 py-1 rounded border ${sim.environment_mode === "b2b_saas" ? "text-[#00F0FF] border-[#00F0FF]/30" : "text-[#007AFF] border-[#007AFF]/30"}`}>
          {sim.environment_mode === "b2b_saas" ? "B2B SAAS" : "B2C CONSUMER"}
        </span>
        {isApp ? (
          <span className="sim-mono text-sm text-[#F5F5F5] flex items-center gap-1.5 truncate max-w-[52vw]" data-testid="sim-app-target">
            {sim.target_url}
          </span>
        ) : (
          <a href={sim.target_url} target="_blank" rel="noreferrer" className="sim-mono text-sm text-[#F5F5F5] hover:text-[#00F0FF] transition-colors flex items-center gap-1.5 truncate max-w-[52vw]">
            {sim.target_url} <ExternalLink className="w-3 h-3 shrink-0" />
          </a>
        )}
        <div className="ml-auto flex items-center gap-3">
          {sim.status === "COMPLETED" && (
            <button
              data-testid="sim-rerun-toggle"
              onClick={() => setRerunOpen((o) => !o)}
              className="sim-mono text-[11px] text-[#00F0FF] border border-[#00F0FF]/30 bg-[#00F0FF]/5 rounded-lg px-3 py-1.5 flex items-center gap-1.5 hover:bg-[#00F0FF]/10 transition-colors"
            >
              <RotateCcw className="w-3 h-3" /> Re-run &amp; compare
            </button>
          )}
          {running && <Loader2 className="w-3.5 h-3.5 text-[#00F0FF] animate-spin" />}
          <span className={`sim-mono text-[11px] ${failed ? "text-[#EF4444]" : running ? "text-[#F59E0B]" : "text-[#10B981]"}`}>{statusLabel}</span>
        </div>
      </div>

      {rerunOpen && sim.status === "COMPLETED" && (
        <RerunPanel sim={sim} onClose={() => setRerunOpen(false)} />
      )}

      {sim.baseline && s && (
        <div className="mb-5">
          <CompareDelta baseline={sim.baseline} current={s} onOpenBaseline={() => navigate(`/sim/s/${sim.baseline.sim_id}`)} />
        </div>
      )}

      {failed && (
        <div className="sim-card rounded-xl p-5 mb-5 border-l-2 border-l-[#EF4444]">
          <div className="flex items-center gap-2 mb-1"><XCircle className="w-4 h-4 text-[#EF4444]" /><span className="text-[#F5F5F5] font-semibold">Could not complete this simulation</span></div>
          <p className="text-[#D4D4D8] text-sm">{sim.error || "The target page could not be rendered. It may block automated browsers or be unreachable."}</p>
          <button onClick={() => navigate("/sim")} className="mt-3 sim-mono text-xs text-[#00F0FF] flex items-center gap-1.5"><RotateCcw className="w-3 h-3" /> Try another URL</button>
        </div>
      )}

      {sim.render?.login_note && (
        <div className="sim-card rounded-xl p-4 mb-5 border-l-2 border-l-[#F59E0B]" data-testid="sim-login-note">
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 text-[#F59E0B] mt-0.5 shrink-0" />
            <p className="text-[#D4D4D8] text-[13px] leading-relaxed">{sim.render.login_note}</p>
          </div>
        </div>
      )}

      {/* App package metadata (upload runs) */}
      {isApp && sim.app_meta && (
        <AppDetails platform={sim.app_platform} meta={sim.app_meta} />
      )}

      {/* Split view: canvas + live feed */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 mb-6">
        <div className="lg:col-span-8">
          <HeatmapOverlay image={image} regions={regions} dims={sim.viewport_dims} failed={failed} />
        </div>
        <div className="lg:col-span-4">
          <AgentFeed agents={agents} assignedCount={sim.progress?.total || 9} status={sim.status} />
        </div>
      </div>

      {/* Synthesis */}
      {s && (
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
          <div className="sim-card-elevated rounded-xl p-4">
            <p className="text-[#F5F5F5] text-sm leading-relaxed"><span className="sim-mono text-[10px] text-[#00F0FF] uppercase mr-2">Synthesis</span>{s.executive_summary}</p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
            <div className="lg:col-span-5"><HealthGauge score={s.health_score} tier={s.health_tier} /></div>
            <div className="lg:col-span-7 grid grid-cols-2 gap-4">
              <Stat icon={Target} label="Predicted CVR" value={`${(s.predicted_conversion_rate * 100).toFixed(2)}%`}
                sub={`benchmark ${(s.benchmark_conversion_rate * 100).toFixed(1)}%`}
                color={s.predicted_conversion_rate >= s.benchmark_conversion_rate ? "#10B981" : "#F59E0B"} />
              <Stat icon={Clock} label="Time to Comprehend" value={`${s.time_to_first_comprehension_sec}s`} sub="first value hook" />
              <Stat icon={CheckCircle2} label="Would Convert" value={`${s.verdict_counts?.APPROVED || 0}/9`} sub="approved agents" color="#10B981" />
              <Stat icon={MinusCircle} label="Dropped Off" value={`${s.verdict_counts?.DROPPED_OFF || 0}/9`} sub="abandoned" color="#EF4444" />
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
            <div className="lg:col-span-5"><ObjectionRadar radar={s.radar_metrics} /></div>
            <div className="lg:col-span-7"><RemediationBacklog backlog={s.remediation_backlog} /></div>
          </div>

          {s.growth_recommendations?.length > 0 && (
            <GrowthRecommendations recommendations={s.growth_recommendations} />
          )}
        </motion.div>
      )}

      {/* Scorecard */}
      {agents.length > 0 && (
        <div className="mt-6"><PersonaScorecard agents={agents} /></div>
      )}

      {running && !s && (
        <div className="flex items-center justify-center gap-2 py-8">
          <TrendingUp className="w-4 h-4 text-[#00F0FF] animate-pulse" />
          <span className="sim-mono text-[11px] text-[#C2C5CE]">Synthesizing econometric scorecard once all agents report…</span>
        </div>
      )}
    </SimLayout>
  );
}
